import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Heading,
  Spinner,
  VStack,
  Button,
  HStack,
} from '@chakra-ui/react';
import { TestCard } from '../components/UserOrders/TestCard';
import { PackageCard } from '../components/UserOrders/PackageCard';
import { getUser } from '../cookie/user';
import { getUserOrders } from '../api/order';
import { useLocation, useNavigate } from 'react-router-dom';

export const OrdersPage = () => {
  const user = getUser();
  const location = useLocation();
  const navigate = useNavigate();
  const searchObject = new URLSearchParams(location?.search);
  const status = searchObject.get('status') || 'all';
  
  const [allOrders, setAllOrders] = useState([]);
  const [displayedOrders, setDisplayedOrders] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(status);
  const [loading, setLoading] = useState(false);

  const orderFiltering = useCallback(async status => {
    try {
      setLoading(true);
      if (status === 'all' || !status) {
        setDisplayedOrders(allOrders);
      } else if (status === 'completed') {
        const completedOrders = allOrders?.filter(
          order => order?.orderStatus === 'completed'
        );
        setDisplayedOrders(completedOrders);
      } else if (status === 'cancelled') {
        const cancelledOrders = allOrders?.filter(
          order => order?.orderStatus === 'cancelled'
        );
        setDisplayedOrders(cancelledOrders);
      } else if (status === 'active') {
        const activeOrders = allOrders?.filter(
          order => order?.orderStatus !== 'completed' && order?.orderStatus !== 'cancelled'
        );
        setDisplayedOrders(activeOrders);
      } else {
        const activeOrders = allOrders?.filter(
          order => order?.orderStatus !== 'completed' && order?.orderStatus !== 'cancelled'
        );
        setDisplayedOrders(activeOrders);
      }
    } catch (error) {
      console.error('error while filtering orders', error);
    } finally {
      setLoading(false);
    }
  }, [allOrders]);

  const fetchData = useCallback(async () => {
    const userId = user?._id || user?.id;
    if (userId) {
      try {
        setLoading(true);
        const res = await getUserOrders(userId);
        const sortedOrders = res?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setAllOrders(sortedOrders);
        if (!status || status === 'all') {
          navigate(`/profile/orders?status=all`, { replace: true });
          setDisplayedOrders(sortedOrders);
        } else {
          orderFiltering(status);
        }
      } catch (error) {
        console.error('Error while fetching orders');
      } finally {
        setLoading(false);
      }
    }
  }, [user?._id, user?.id, status, navigate, orderFiltering]);



  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusFromUrl = params.get('status');
    orderFiltering(statusFromUrl);
    setSelectedStatus(statusFromUrl);
  }, [location.search, orderFiltering]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '60vh',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spinner size={'lg'} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            maxWidth: '100%',
            flexDir: 'column',
            px: ['3', '5', '10', '20'],
            py: 5,
            gap: 5,
            zIndex: '1000',
            pb: '150px',
          }}
        >
          <VStack
            width="100%"
            maxW="800px"
            align="flex-start"
            spacing={8}
            mx="auto"
          >
            <Heading color={'#0C727E'} size="lg" alignSelf={'center'}>My Orders</Heading>
            
            <HStack spacing={4} width="100%" justify="flex-start" overflowX="auto" pb={2}>
              {['all', 'active', 'completed', 'cancelled'].map((tab) => (
                <Button
                  key={tab}
                  onClick={() => {
                    setSelectedStatus(tab);
                    navigate(`/profile/orders?status=${tab}`);
                  }}
                  variant={selectedStatus === tab || (tab === 'all' && !selectedStatus) ? 'solid' : 'outline'}
                  colorScheme={selectedStatus === tab || (tab === 'all' && !selectedStatus) ? 'teal' : 'gray'}
                  borderRadius="full"
                  px={8}
                  size="md"
                  textTransform="capitalize"
                >
                  {tab}
                </Button>
              ))}
            </HStack>

            {displayedOrders?.length === 0 ? (
              <Heading size="md">No {selectedStatus || 'pending'} orders</Heading>
            ) : (
              <VStack spacing={6} width="100%">
                {displayedOrders?.map((order, idx) => {
                  if (order?.test && Object.keys(order.test).length > 0) {
                    return <Box key={`test-${order._id || idx}`} w="100%"><TestCard test={order?.test} order={order} /></Box>;
                  }
                  if (order?.packages?.tests) {
                    return <Box key={`pkg-${order._id || idx}`} w="100%"><PackageCard packages={order?.packages} order={order} /></Box>;
                  }
                  return null;
                })}
              </VStack>
            )}
          </VStack>
        </Box>
      )}
    </>
  );
};
